import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Trash2 } from 'lucide-react';

const DisconnectModal = ({
  isOpen,
  onClose,
  onConfirm,
  integrationName,
  isLoading,
  showPurgeOption = false,
  purgeLabel = 'Also delete synced product content from the knowledge base',
}) => {
  const [purgeContent, setPurgeContent] = useState(false);

  useEffect(() => {
    if (isOpen) setPurgeContent(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (showPurgeOption) {
      onConfirm({ purgeContent });
    } else {
      onConfirm();
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={!isLoading ? onClose : undefined}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white border border-gray-200 rounded-[2rem] overflow-hidden shadow-2xl"
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            className="absolute right-6 top-6 p-2 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>

          <div className="p-8 pt-10 text-center">
            <div className="w-20 h-20 bg-red-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-red-100">
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>

            <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">
              Disconnect {integrationName}?
            </h3>

            <p className="text-gray-500 mb-6 leading-relaxed font-medium px-2">
              This will stop automations, clear API credentials, and unregister webhooks for this{' '}
              {integrationName} connection.
            </p>

            {showPurgeOption && (
              <label className="flex items-start gap-3 text-left mb-6 px-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={purgeContent}
                  onChange={(e) => setPurgeContent(e.target.checked)}
                  disabled={isLoading}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-600 font-medium">{purgeLabel}</span>
              </label>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-600 text-white font-bold rounded-2xl hover:bg-red-700 transition-all shadow-lg shadow-red-600/10 disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Trash2 className="h-5 w-5" />
                    </motion.div>
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-5 w-5" />
                    Yes, Disconnect
                  </>
                )}
              </button>

              <button
                onClick={onClose}
                disabled={isLoading}
                className="w-full px-6 py-4 bg-gray-50 text-gray-900 font-bold rounded-2xl hover:bg-gray-100 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 text-center uppercase tracking-widest font-bold">
              This action cannot be undone
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default DisconnectModal;
